import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchFittingTypes, createFittingType, deleteFittingType, updateFittingType, createConnTypeBodySize, fetchMeasureUnit, createConnTypeConnSize, fetchConnTypeConnSizeById, fetchConnTypeBSByConnSizeID, updateConnTypeBodySize, deleteConnTypeBodySize, deleteConnTypeConnSize, updateConnTypeConnSize, updateSubComponent, addSubComponent, SubCompByConnTypeID, deleteSubComp } from '../../common/jsonstore';
import { FittingType, ConnTypeBodySize, ConnTypeConnSize, MeasuringUnit, ConnTypeSubComp } from '../../common/model';
import { AppState } from '../../redux/store/rootReducer';
import { AppDispatch } from '../../redux/store/store';
import { isTokenExpired } from '../../services/authenticationService';
import { signOut } from '../../services/localStorage';
import { handleError } from '../../services/messageApiService';

export interface IAuthentication {
    FittingType: FittingType[];
    ConnTypeBodySize: ConnTypeBodySize[];
    ConnTypeSubComp: ConnTypeSubComp[];
    MeasuringUnit: MeasuringUnit[];
    ConnTypeConnSize: ConnTypeConnSize[];
    success: any;
    isProcessingComplete: boolean;
}
const initialState: IAuthentication = { FittingType: [], ConnTypeBodySize: [], MeasuringUnit: [], ConnTypeConnSize: [], ConnTypeSubComp: [], success: {}, isProcessingComplete: false };

const FittingTypeSlice = createSlice({
    name: 'FittingTypes',
    initialState,
    reducers: {
        receiveFittingTypes: (state, action: PayloadAction<any>) => {
            return {
                ...state,
                FittingType: action.payload,
                isProcessingComplete: false
            };
        },
        receiveFittingType: (state, action: PayloadAction<any>) => {
            return {
                ...state,
                FittingType: action.payload,
                isProcessingComplete: false
            };
        },
        receiveConnTypeBodySize: (state, action: PayloadAction<any>) => {
            return {
                ...state,
                ConnTypeBodySize: action.payload,
                isProcessingComplete: false
            };
        },
        receiveMeasuringUnit: (state, action: PayloadAction<any>) => {
            return {
                ...state,
                MeasuringUnit: action.payload,
                isProcessingComplete: false
            };
        },
        receiveConnTypeConnSize: (state, action: PayloadAction<any>) => {
            return {
                ...state,
                ConnTypeConnSize: action.payload,
                isProcessingComplete: false
            };
        },
        receiveConnTypeSubComp: (state, action: PayloadAction<any>) => {
            return {
                ...state,
                ConnTypeSubComp: action.payload,
                isProcessingComplete: false
            };
        },
        success: (state, action: PayloadAction<any>) => {
            return {
                ...state,
                success: action.payload,
                isProcessingComplete: true
            };
        }
    }
});

export const loadFittingTypes = (): any => async (dispatch: AppDispatch) => {
    if (isTokenExpired()) {
        const FittingTypes = await fetchFittingTypes().then((res: any) => {
            dispatch(FittingTypeSlice.actions.receiveFittingTypes(res))
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Connection Type not loaded';
                handleError(msg);
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}

export const addFittingType = (newFittingType: any): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (isTokenExpired()) {
        const res: any = await createFittingType(newFittingType).then((resp: any) => {
            dispatch(FittingTypeSlice.actions.receiveFittingTypes(resp.data))
            dispatch(FittingTypeSlice.actions.success({ 'sts': true, data: resp?.msg }));
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Connection Type not added';
                dispatch(FittingTypeSlice.actions.success({ 'sts': false, data: msg }));
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}

export const modifyFittingType = (FittingType: any, id: number): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (isTokenExpired()) {
        const res = await updateFittingType(FittingType, id).then((resp: any) => {
            dispatch(FittingTypeSlice.actions.receiveFittingTypes(resp.data))
            dispatch(FittingTypeSlice.actions.success({ 'sts': true, data: resp?.msg }));
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Connection Type not updated';
                dispatch(FittingTypeSlice.actions.success({ 'sts': false, data: msg }));
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}

export const removeFittingType = (id: number): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (isTokenExpired()) {
        await deleteFittingType(id).then((res) => {
            const updatedFittingTypes = getState().FittingTypes.FittingType.filter((item) => item.id !== id);
            dispatch(FittingTypeSlice.actions.receiveFittingTypes(updatedFittingTypes));
            dispatch(FittingTypeSlice.actions.success({ 'sts': true, data: 'Connection Type deleted Successfully' }));
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = ''
                if (err?.response?.data?.data?.constraint?.includes("FK")) {
                    msg = 'Connection Type not deleted,It is being used in its mapping data'
                }
                else if (err?.response?.data?.msg) { msg = err?.response?.data?.msg }
                else { msg = 'Connection Type not deleted' }
                dispatch(FittingTypeSlice.actions.success({ 'sts': false, data: msg }));
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}


export const updateSts = (data: any): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    dispatch(FittingTypeSlice.actions.success(data));
}

export const addConnTypeBodySize = (data: any): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (isTokenExpired()) {
        const res: any = await createConnTypeBodySize(data).then((resp: any) => {
            dispatch(FittingTypeSlice.actions.receiveConnTypeBodySize(resp?.data));
            dispatch(FittingTypeSlice.actions.success({ 'sts': true, data: ['addBS', resp?.msg] }));
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Body Size not added';
                dispatch(FittingTypeSlice.actions.success({ 'sts': false, data: msg }));
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}


export const loadBodySizeByConnSizeID = (id: number): any => async (dispatch: AppDispatch) => {
    if (isTokenExpired()) {
        const data = await fetchConnTypeBSByConnSizeID(id).then((res: any) => {
            dispatch(FittingTypeSlice.actions.receiveConnTypeBodySize(res?.data))
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Data not loaded';
                handleError(msg);
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}


export const modifyConnTypeBodySize = (newCat: any, id: number): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (isTokenExpired()) {
        const res: any = await updateConnTypeBodySize(newCat, id).then((resp: any) => {
            dispatch(FittingTypeSlice.actions.receiveConnTypeBodySize(resp?.data));
            dispatch(FittingTypeSlice.actions.success({ 'sts': true, data: ['updtBS', resp?.msg] }));
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Data not Updated';
                dispatch(FittingTypeSlice.actions.success({ 'sts': false, data: msg }));
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}

  

export const removeConnTypeBodySize = (id: number): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (isTokenExpired()) {
        await deleteConnTypeBodySize(id).then((res) => {
            const dataa = getState().FittingTypes.ConnTypeBodySize.filter((item) => item.id !== id);
            dispatch(FittingTypeSlice.actions.receiveConnTypeBodySize(dataa));
            dispatch(FittingTypeSlice.actions.success({ 'sts': true, data: ['dltBS', 'Body Size deleted Successfully'] }));
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = ''
                if (err?.response?.data?.data?.constraint?.includes("FK")) {
                    msg = 'Body Size not deleted'
                } else if (err?.response?.data?.msg) {
                    msg = err?.response?.data?.msg;
                } else {
                    msg = 'Body Size not deleted';
                }
                dispatch(FittingTypeSlice.actions.success({ 'sts': false, data: msg }));
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}


export const loadMeasureUnit = (): any => async (dispatch: AppDispatch) => {
    if (isTokenExpired()) {
        const data = await fetchMeasureUnit().then((res: any) => {
            dispatch(FittingTypeSlice.actions.receiveMeasuringUnit(res))
        })
            .catch(err => {
                if (err?.response?.data?.message?.includes("access token")) {
                    handleError(err.response.data.message);
                    setTimeout(() => { return signOut(); }, 500);
                } else {
                    let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Measuring Unit not loaded';
                    handleError(msg);
                }
            })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}

export const addConnTypeConnSize = (data: any): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (isTokenExpired()) {
        const res: any = await createConnTypeConnSize(data).then((resp: any) => {
            dispatch(FittingTypeSlice.actions.receiveConnTypeConnSize(resp?.data));
            dispatch(FittingTypeSlice.actions.success({ 'sts': true, data: ['addCS', resp?.msg] }));
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Connection size not added';
                dispatch(FittingTypeSlice.actions.success({ 'sts': false, data: msg }));
            }
        })
        // dispatch(CategorySlice.actions.receiveUser(res?.data));
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}


export const modifyConnTypeConnSize = (data: any, id: number): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (isTokenExpired()) {
        const res: any = await updateConnTypeConnSize(data, id).then((resp: any) => {
            dispatch(FittingTypeSlice.actions.receiveConnTypeConnSize(resp?.data));
            dispatch(FittingTypeSlice.actions.success({ 'sts': true, data: ['updtCS', resp?.msg] }));
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Connection Size not updaed';
                dispatch(FittingTypeSlice.actions.success({ 'sts': false, data: msg }));
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}

export const loadConnTypeConnSizeByTypeID = (id: number): any => async (dispatch: AppDispatch) => {
    if (isTokenExpired()) {
        const data = await fetchConnTypeConnSizeById(id).then((res: any) => {
            dispatch(FittingTypeSlice.actions.receiveConnTypeConnSize(res?.data))
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Data not loaded';
                handleError(msg);
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}

export const removeConnTypeConnSize = (id: number): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (isTokenExpired()) {
        await deleteConnTypeConnSize(id).then((res) => {
            const data = getState().FittingTypes.ConnTypeConnSize.filter((item) => item.id !== id);
            dispatch(FittingTypeSlice.actions.receiveConnTypeConnSize(data));
            dispatch(FittingTypeSlice.actions.success({ 'sts': true, data: ['dltCS', 'Connection Size deleted Successfully'] }));
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = ''
                if (err?.response?.data?.data?.constraint?.includes("FK")) {
                    msg = 'Connection size not deleted,It is being used in body size'
                }
                else if (err?.response?.data?.msg) { msg = err?.response?.data?.msg }
                else { msg = 'Connection Size not deleted'; }
                dispatch(FittingTypeSlice.actions.success({ 'sts': false, data: msg }));
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}

export const modifyConnTypeSubComp = (data: any, id: number): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (isTokenExpired()) {
      const res: any = await updateSubComponent(data, id).then((resp: any) => {
        dispatch(FittingTypeSlice.actions.receiveConnTypeSubComp(resp?.data))
        dispatch(FittingTypeSlice.actions.success({ 'sts': true, data: ['SubComponent updated successfully'] }));
      }).catch(err => {
        if (err?.response?.data?.message?.includes("access token")) {
          handleError(err.response.data.message);
          setTimeout(() => { return signOut(); }, 500);
        } else {
          let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'SubComponent Not Updated';
          dispatch(FittingTypeSlice.actions.success({ 'sts': false, data: msg }));
        }
      })
    } else {
      handleError('Token is expired');
      setTimeout(() => { return signOut(); }, 500);
    }
  }


export const addConnTypeSubComp = (data: any): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (isTokenExpired()) {
      const res: any = await addSubComponent(data).then((resp: any) => {
        dispatch(FittingTypeSlice.actions.receiveConnTypeSubComp(resp?.data))
        dispatch(FittingTypeSlice.actions.success({ 'sts': true, data: ['SubComponent added successfully'] }));
      }).catch(err => {
        if (err?.response?.data?.message?.includes("access token")) {
          handleError(err.response.data.message);
          setTimeout(() => { return signOut(); }, 500);
        } else {
          let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Data added';
          dispatch(FittingTypeSlice.actions.success({ 'sts': false, data: msg }));
        }
      })
    } else {
      handleError('Token is expired');
      setTimeout(() => { return signOut(); }, 500);
    }
  }

  export const loadSubCompByConnTypeID = (id: number): any => async (dispatch: AppDispatch) => {
    if (isTokenExpired()) {
        const data = await SubCompByConnTypeID(id).then((res: any) => {
            dispatch(FittingTypeSlice.actions.receiveConnTypeSubComp(res?.data))
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = (err?.response?.data?.msg) ? err?.response?.data?.msg : 'Data not loaded';
                handleError(msg);
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}

export const removeSubComp = (id: number, con_type_id:number): any => async (dispatch: AppDispatch, getState: () => AppState) => {
    if (isTokenExpired()) {
        await deleteSubComp(id,con_type_id).then((res:any) => {
            dispatch(FittingTypeSlice.actions.receiveConnTypeSubComp(res?.data))
            dispatch(FittingTypeSlice.actions.success({ 'sts': true, data: ['SubComponent deleted successfully'] }));
        }).catch(err => {
            if (err?.response?.data?.message?.includes("access token")) {
                handleError(err.response.data.message);
                setTimeout(() => { return signOut(); }, 500);
            } else {
                let msg = ''
                if (err?.response?.data?.data?.constraint?.includes("FK")) {
                    msg = 'Data not deleted'
                } else if (err?.response?.data?.msg) {
                    msg = err?.response?.data?.msg;
                } else {
                    msg = 'Data not deleted';
                }
                dispatch(FittingTypeSlice.actions.success({ 'sts': false, data: msg }));
            }
        })
    } else {
        handleError('Token is expired');
        setTimeout(() => { return signOut(); }, 500);
    }
}
export default FittingTypeSlice.reducer;
